import { useEffect, useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  AudioLines,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Play,
  Square,
  Sparkles,
} from "lucide-react";
import {
  fetchProjectProfile,
  fetchProjectAudio,
  saveAudioSelection,
  saveVoiceInstructions,
  fetchVoicePreview,
  synthesizeScenarioAudio,
  suggestSfxPositions,
  fetchScenarioAudio,
} from "@/api/client";
import { useSessionStore } from "@/hooks/useSessionStore";
import { ELEVEN_LABS_VOICES } from "@/constants/voices";
import { Dialog, DialogContent } from "@/components/ui/dialog";

type AiProvider = "eleven_labs" | "qwen_local";

export function SyntheseVocaleView() {
  const navigate = useNavigate();
  const { sessionId, projectName, lastProjectName, setCurrentStep } = useSessionStore();
  const queryClient = useQueryClient();
  const resolvedProjectName = projectName ?? lastProjectName;

  const [aiProvider, setAiProvider] = useState<AiProvider>("eleven_labs");
  const [ttsVoiceId, setTtsVoiceId] = useState<string>(ELEVEN_LABS_VOICES[0].id);
  const [voiceInstructions, setVoiceInstructions] = useState("");

  const [showAiDropdown, setShowAiDropdown] = useState(false);
  const [showVoiceDropdown, setShowVoiceDropdown] = useState(false);

  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const [loadingPreviewId, setLoadingPreviewId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  const aiDropdownRef = useRef<HTMLDivElement | null>(null);
  const voiceDropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        aiDropdownRef.current &&
        !aiDropdownRef.current.contains(event.target as Node)
      ) {
        setShowAiDropdown(false);
      }
      if (
        voiceDropdownRef.current &&
        !voiceDropdownRef.current.contains(event.target as Node)
      ) {
        setShowVoiceDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const profileQuery = useQuery({
    queryKey: ["project-profile", resolvedProjectName],
    queryFn: () => fetchProjectProfile(resolvedProjectName!),
    enabled: Boolean(resolvedProjectName),
  });

  const projectAudioQuery = useQuery({
    queryKey: ["project-audio", resolvedProjectName],
    queryFn: () => fetchProjectAudio(resolvedProjectName!),
    enabled: Boolean(resolvedProjectName),
  });

  const audioQuery = useQuery({
    queryKey: ["scenario-audio", sessionId],
    queryFn: () => fetchScenarioAudio(sessionId!),
    enabled: Boolean(sessionId),
    refetchInterval: isGenerating ? 2000 : false,
  });

  useEffect(() => {
    if (profileQuery.data) {
      // Force Eleven Labs for now since Qwen local is disabled
      setAiProvider("eleven_labs");
      if (profileQuery.data.tts_voice_id) {
        setTtsVoiceId(profileQuery.data.tts_voice_id);
      }
      if (profileQuery.data.voice_instructions) {
        setVoiceInstructions(profileQuery.data.voice_instructions);
      }
    }
  }, [profileQuery.data]);

  useEffect(() => {
    if (isGenerating && audioQuery.data) {
      const status = audioQuery.data.status;
      if (status && status !== "pending" && status !== "running") {
        setIsGenerating(false);
        if (status === "failed") {
          setGenerateError(
            audioQuery.data.error || "Erreur lors de la génération de l'audio."
          );
        } else {
          // Success! Invalidate and navigate to mixing editor
          queryClient.invalidateQueries({ queryKey: ["scenario-audio", sessionId] });
          queryClient.invalidateQueries({ queryKey: ["project-profile", resolvedProjectName] });
          setCurrentStep("scenario_edit");
          navigate("/step/scenario_edit");
        }
      }
    }
  }, [
    audioQuery.data,
    isGenerating,
    navigate,
    queryClient,
    resolvedProjectName,
    sessionId,
    setCurrentStep,
  ]);

  const handlePlayPreview = async (e: React.MouseEvent, voiceId: string) => {
    e.stopPropagation();
    const wasPlayingThis = playingVoiceId === voiceId || loadingPreviewId === voiceId;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPlayingVoiceId(null);
    setLoadingPreviewId(null);
    if (wasPlayingThis) return;
    setLoadingPreviewId(voiceId);
    try {
      const blob = await fetchVoicePreview(voiceId);
      const url = URL.createObjectURL(blob);
      previewUrlRef.current = url;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        setPlayingVoiceId(null);
        audioRef.current = null;
        if (previewUrlRef.current) {
          URL.revokeObjectURL(previewUrlRef.current);
          previewUrlRef.current = null;
        }
      };
      setLoadingPreviewId(null);
      setPlayingVoiceId(voiceId);
      audio.play();
    } catch {
      setLoadingPreviewId(null);
      setPlayingVoiceId(null);
    }
  };

  const handleGenerate = async () => {
    if (!sessionId || !resolvedProjectName) return;
    setGenerateError(null);
    setIsGenerating(true);

    try {
      const projectAudio = projectAudioQuery.data ?? [];
      if (projectAudio.length === 0) {
        throw new Error(
          "Aucun fichier audio dans ce projet. Importez d'abord vos sources audio."
        );
      }

      // Save Qwen instructions if local is selected
      if (aiProvider === "qwen_local" && voiceInstructions.trim()) {
        await saveVoiceInstructions(resolvedProjectName, voiceInstructions.trim());
      }

      const ttsProvider = aiProvider === "qwen_local" ? "qwen" : "elevenlabs";
      await saveAudioSelection(sessionId, {
        project_name: resolvedProjectName,
        voices: projectAudio.slice(0, 3),
        backgrounds: { ambient: null, punctual: [] },
        auto_backgrounds: false,
        tts_provider: ttsProvider,
        tts_voice_id: aiProvider === "eleven_labs" ? ttsVoiceId : null,
      });

      // Fire-and-forget SFX position suggestion
      suggestSfxPositions(sessionId).catch(() => {/* best-effort */});

      // Synthesize scenario audio
      await synthesizeScenarioAudio(sessionId);

      // Invalidate queries so scenario edit gets the new audio key
      queryClient.invalidateQueries({ queryKey: ["scenario-audio", sessionId] });
      queryClient.invalidateQueries({ queryKey: ["project-profile", resolvedProjectName] });

      // Note: We no longer navigate immediately.
      // We keep isGenerating = true and wait for the useEffect polling to complete the process.
    } catch (err: any) {
      let msg = "Erreur lors de la génération.";
      if (err && typeof err === "object" && err.response?.data?.detail) {
        msg = err.response.data.detail;
      } else if (err instanceof Error) {
        msg = err.message;
      }
      if (msg.includes("ELEVENLABS_API_KEY")) {
        msg = "La clé API d'ElevenLabs (ELEVENLABS_API_KEY) est absente du fichier .env. Veuillez la configurer dans l'environnement du serveur ou basculer sur le moteur Qwen local.";
      }
      setGenerateError(msg);
      setIsGenerating(false);
    }
  };

  const handleRetour = () => {
    setCurrentStep("edition_text");
    navigate("/step/edition_text");
  };

  const selectedAi = aiProvider === "eleven_labs" ? "Eleven Labs" : "Qwen local";
  const selectedVoice = ELEVEN_LABS_VOICES.find((v) => v.id === ttsVoiceId) ?? ELEVEN_LABS_VOICES[0];

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-6 p-6">
      <section className="w-full rounded-[14px] border border-[#E2E8F0] bg-white shadow-[0_2px_10px_rgba(0,0,0,0.10)]">
        <div className="flex items-center gap-6 border-b border-[#F4F4F4] bg-[#F8FAFC] px-5 py-4 rounded-t-[13px]">
          <div className="flex min-w-0 flex-col gap-1">
            <div className="inline-flex items-center gap-2">
              <AudioLines className="h-5 w-5 text-[#0F172B]" />
              <h2 className="text-[20px] font-semibold leading-none text-[#0F172B]">Synthèse vocale</h2>
            </div>
            <p className="text-[14px] font-medium leading-5 text-[#45556C]">
              Configurer les paramètres de la synthèse vocale pour générer la narration.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-6 p-6">
          {/* AI Provider selector */}
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-[#45556C]">Modèle de synthèse vocale :</span>
            <div className="relative inline-block" ref={aiDropdownRef}>
              <button
                type="button"
                onClick={() => {
                  setShowAiDropdown(!showAiDropdown);
                  setShowVoiceDropdown(false);
                }}
                className="inline-flex h-[38px] items-center gap-2 rounded-xl border border-[#007AFF] bg-[#EFF6FF] px-3 text-sm font-medium text-[#007AFF] transition-colors hover:bg-[#e6f2ff]"
              >
                <Sparkles className="h-4 w-4 text-[#007AFF]" />
                <span>{selectedAi}</span>
                <ChevronDown className="h-4 w-4 text-[#007AFF]" />
              </button>

              {showAiDropdown && (
                <div className="absolute left-0 top-full mt-1.5 z-30 w-[340px] overflow-hidden rounded-xl border border-[#E2E8F0] bg-white shadow-[0_4px_16px_rgba(0,0,0,0.12)]">
                  <button
                    type="button"
                    onClick={() => {
                      setAiProvider("eleven_labs");
                      setShowAiDropdown(false);
                    }}
                    className="flex w-full items-start justify-between gap-[14px] px-5 py-4 text-left transition-colors hover:bg-[#F8FAFC] border-b border-[#E2E8F0]"
                  >
                    <div className="flex flex-col gap-1">
                      <span className="text-[15px] font-semibold text-[#0F172B]">Eleven Labs</span>
                      <span className="text-[13px] text-[#45556C]">Voix expressives, qualité élevée.</span>
                    </div>
                    {aiProvider === "eleven_labs" && <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#007AFF]" />}
                  </button>
                  <button
                    type="button"
                    disabled
                    className="flex w-full items-start justify-between gap-[14px] px-5 py-4 text-left opacity-50 cursor-not-allowed bg-slate-50"
                  >
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[15px] font-semibold text-[#64748B]">Qwen local</span>
                        <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">
                          Bientôt
                        </span>
                      </div>
                      <span className="text-[13px] text-[#94A3B8]">Plus de personnalisation locale.</span>
                    </div>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* ElevenLabs voice picker */}
          {aiProvider === "eleven_labs" ? (
            <div className="flex flex-col gap-2 max-w-[500px]">
              <label className="text-[14px] font-medium leading-5 text-[#0F172B]">Choisir une voix</label>
              <div className="relative" ref={voiceDropdownRef}>
                <div className="inline-flex min-h-[66px] w-full items-center justify-between rounded-lg border border-[#E2E8F0] bg-[#F4F4F4] p-3 transition-colors hover:bg-[#eef2f7]">
                  <button
                    type="button"
                    className="flex flex-1 items-center gap-2 text-left"
                    onClick={() => {
                      setShowVoiceDropdown(!showVoiceDropdown);
                      setShowAiDropdown(false);
                    }}
                  >
                    <AudioLines className="h-4 w-4 shrink-0 text-[#0F172B]" />
                    <span className="flex flex-col">
                      <span className="text-[14px] font-medium text-[#0F172B]">{selectedVoice.name}</span>
                      <span className="text-[12px] font-normal text-[#45556C]">{selectedVoice.descriptor}</span>
                    </span>
                  </button>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={(e) => handlePlayPreview(e, selectedVoice.id)}
                      className="inline-flex h-7 w-7 items-center justify-center rounded-full transition-colors hover:bg-[#dde3ec]"
                      title="Écouter un aperçu"
                    >
                      {loadingPreviewId === selectedVoice.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" />
                      ) : playingVoiceId === selectedVoice.id ? (
                        <Square className="h-3.5 w-3.5 fill-current text-blue-500" />
                      ) : (
                        <Play className="h-3.5 w-3.5 text-[#45556C]" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowVoiceDropdown(!showVoiceDropdown);
                        setShowAiDropdown(false);
                      }}
                    >
                      <ChevronDown className="h-4 w-4 text-[#45556C]" />
                    </button>
                  </div>
                </div>

                {showVoiceDropdown && (
                  <div className="absolute left-0 right-0 top-full mt-1.5 z-30 max-h-[260px] overflow-y-auto rounded-xl border border-[#E2E8F0] bg-white shadow-[0_4px_16px_rgba(0,0,0,0.12)]">
                    {ELEVEN_LABS_VOICES.map((voice, index) => {
                      const isActive = voice.id === ttsVoiceId;
                      return (
                        <div
                          key={voice.id}
                          onClick={() => {
                            setTtsVoiceId(voice.id);
                            setShowVoiceDropdown(false);
                          }}
                          className={`flex w-full items-center justify-between gap-2 px-4 py-3.5 transition-colors hover:bg-[#F8FAFC] cursor-pointer ${
                            index < ELEVEN_LABS_VOICES.length - 1 ? "border-b border-[#E2E8F0]" : ""
                          }`}
                        >
                          <div className="flex flex-1 items-start gap-3.5 text-left">
                            <AudioLines className="mt-0.5 h-4 w-4 shrink-0 text-[#0F172B]" />
                            <span className="flex flex-col">
                              <span className="text-[14px] font-semibold leading-tight text-[#0F172B]">{voice.name}</span>
                              <span className="text-[12px] font-normal leading-snug text-[#45556C] mt-0.5">{voice.descriptor}</span>
                            </span>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              type="button"
                              onClick={(e) => handlePlayPreview(e, voice.id)}
                              className="inline-flex h-7 w-7 items-center justify-center rounded-full transition-colors hover:bg-[#dde3ec]"
                              title="Écouter un aperçu"
                            >
                              {loadingPreviewId === voice.id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" />
                              ) : playingVoiceId === voice.id ? (
                                <Square className="h-3.5 w-3.5 fill-current text-blue-500" />
                              ) : (
                                <Play className="h-3.5 w-3.5 text-[#45556C]" />
                              )}
                            </button>
                            {isActive ? <Check className="h-4 w-4 text-[#007AFF]" /> : <span className="w-4" />}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Qwen Local voice instructions */
            <div className="flex flex-col gap-2 max-w-[600px]">
              <label className="text-[14px] font-medium leading-5 text-[#0F172B]">
                Instructions de voix
              </label>
              <textarea
                value={voiceInstructions}
                onChange={(e) => setVoiceInstructions(e.target.value)}
                placeholder="Ex. : Voix grave et posée, tempo lent, ton nostalgique, légèrement chuchoté, français naturel…"
                rows={3}
                className="w-full resize-none rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2 text-[14px] font-normal leading-snug text-[#45556C] outline-none placeholder:text-[#94a3b8] focus:border-[#623DC7]"
              />
              <p className="text-[12px] font-normal leading-none text-[#94a3b8]">
                Ces instructions guident la synthèse vocale Qwen (ton, rythme, émotions, etc.).
              </p>
            </div>
          )}
        </div>
      </section>

      {generateError && (
        <div className="rounded-lg border border-[#FF3B30] bg-[#fff1f0] px-4 py-3 text-sm text-[#FF3B30]">
          {generateError}
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={handleRetour}
          className="inline-flex h-[38px] items-center gap-1.5 rounded-[12px] border border-[#E2E8F0] bg-white px-4 text-[14px] font-medium text-[#45556C] transition-colors hover:bg-[#F8FAFC]"
        >
          <ChevronLeft className="h-4 w-4" />
          <span>Précédent : Édition du scénario</span>
        </button>

        <button
          type="button"
          onClick={handleGenerate}
          className="inline-flex h-[38px] items-center gap-1.5 rounded-[12px] bg-[#007AFF] px-4 text-[14px] font-medium leading-[14px] text-white transition-colors hover:bg-[#006ae0]"
        >
          <span>Générer l&apos;audio</span>
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Audio Generation Loader Modal */}
      <Dialog open={isGenerating} onOpenChange={() => {}}>
        <DialogContent hideCloseButton className="max-w-md border border-blue-100 bg-white p-6 shadow-xl rounded-2xl animate-in fade-in zoom-in-95 duration-200" onInteractOutside={(e) => e.preventDefault()}>
          <style dangerouslySetInnerHTML={{__html: `
            @keyframes soundwave-pulse {
              0%, 100% { transform: scaleY(0.3); }
              50% { transform: scaleY(1); }
            }
            .soundwave-bar {
              transform-origin: bottom;
              animation: soundwave-pulse 1.2s ease-in-out infinite;
            }
          `}} />
          <div className="flex flex-col items-center text-center">
            {/* Animated Audio Soundwave Visualizer */}
            <div className="flex items-end justify-center gap-1.5 h-12 mb-6">
              <span className="w-1.5 h-full bg-blue-500 rounded-full soundwave-bar" style={{ animationDelay: '0.1s' }} />
              <span className="w-1.5 h-full bg-blue-400 rounded-full soundwave-bar" style={{ animationDelay: '0.3s' }} />
              <span className="w-1.5 h-full bg-blue-600 rounded-full soundwave-bar" style={{ animationDelay: '0.5s' }} />
              <span className="w-1.5 h-full bg-blue-500 rounded-full soundwave-bar" style={{ animationDelay: '0.2s' }} />
              <span className="w-1.5 h-full bg-blue-400 rounded-full soundwave-bar" style={{ animationDelay: '0.4s' }} />
            </div>

            <h3 className="text-xl font-bold text-[#0F172B] tracking-tight">
              Génération de la narration
            </h3>
            
            <p className="text-[#64748B] text-sm font-normal mt-2 max-w-[320px]">
              La synthèse vocale est en cours. Veuillez patienter…
            </p>

            <div className="w-full mt-6 px-4 py-3.5 bg-blue-50/50 border border-blue-100/50 rounded-2xl flex flex-col items-center">
              <span className="text-[10px] font-bold text-blue-600 uppercase tracking-widest mb-1.5">
                Statut
              </span>
              <span className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" />
                {audioQuery.data?.status === "running" ? "Synthèse en cours…" : "Démarrage…"}
              </span>
            </div>

            <p className="text-xs text-slate-400 font-normal mt-5 leading-normal">
              Veuillez ne pas fermer cette fenêtre. Vos fichiers audio seront prêts dans quelques instants…
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
